import type { ReactNode } from "react";
import type { HomeLook, HomeStageId } from "@townsy/contracts";
import { stageLabel } from "../../homes/homeLogic";
import { exteriorLookSummary, floorHex, interiorLookSummary, roofHex, wallHex } from "../../homes/lookPalette";

/**
 * Small original 2D drawings of a home (docs/01 section 7: an accessible 2D equivalent of the 3D
 * look): the exterior for a stage + look, and the inside walls + floor. Purely illustrative — the
 * town renderer draws the real home from the same choices.
 */

function shade(hex: string, factor: number): string {
  const n = parseInt(hex.slice(1), 16);
  const ch = (shift: number) => Math.max(0, Math.min(255, Math.round(((n >> shift) & 255) * factor)));
  return `#${((ch(16) << 16) | (ch(8) << 8) | ch(0)).toString(16).padStart(6, "0")}`;
}

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Windows for a wall box, styled by the look. */
function windows(box: Box, rows: number, cols: number, look: HomeLook, skipCenterBottom: boolean): ReactNode[] {
  const out: ReactNode[] = [];
  const modern = look.style === "modern";
  const ww = modern ? Math.min(18, box.w / (cols * 1.6)) : Math.min(11, box.w / (cols * 2.2));
  const wh = modern ? Math.min(12, box.h / (rows * 1.8)) : Math.min(10, box.h / (rows * 2));
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (skipCenterBottom && r === rows - 1 && cols % 2 === 1 && c === (cols - 1) / 2) continue;
      const cx = box.x + ((c + 0.5) * box.w) / cols;
      const cy = box.y + ((r + 0.45) * box.h) / rows;
      const key = `w${r}-${c}`;
      out.push(
        <g key={key}>
          <rect x={cx - ww / 2} y={cy - wh / 2} width={ww} height={wh} rx={modern ? 0.5 : 1} fill={modern ? "#2B3440" : "#9CC3E6"} stroke={look.style === "classic" ? "#FFFFFF" : shade(wallHex(look.wallColor), 0.6)} strokeWidth={look.style === "classic" ? 1.4 : 0.8} />
          {look.style === "classic" ? (
            <>
              <rect x={cx - ww / 2 - 3.2} y={cy - wh / 2} width={2.6} height={wh} fill={roofHex(look.roofColor)} />
              <rect x={cx + ww / 2 + 0.6} y={cy - wh / 2} width={2.6} height={wh} fill={roofHex(look.roofColor)} />
            </>
          ) : null}
          {look.style === "cottage" ? <rect x={cx - ww / 2 - 1} y={cy + wh / 2} width={ww + 2} height={2.4} fill="#D9667F" /> : null}
        </g>,
      );
    }
  }
  return out;
}

function door(x: number, groundY: number, look: HomeLook, w = 9, h = 15): ReactNode {
  const fill = shade(roofHex(look.roofColor), 1.15);
  if (look.style === "cottage") {
    const r = w / 2;
    return <path key="door" d={`M${x - r} ${groundY} V${groundY - h + r} A${r} ${r} 0 0 1 ${x + r} ${groundY - h + r} V${groundY} Z`} fill={fill} />;
  }
  return <rect key="door" x={x - w / 2} y={groundY - h} width={w} height={h} fill={look.style === "modern" ? "#20262E" : fill} />;
}

function wallDetails(box: Box, look: HomeLook): ReactNode[] {
  const out: ReactNode[] = [];
  if (look.style === "rustic") {
    out.push(<rect key="stone" x={box.x} y={box.y + box.h - 6} width={box.w} height={6} fill="#8E8A82" />);
    for (let i = 1; i < 4; i++) {
      const x = box.x + (i * box.w) / 4;
      out.push(<line key={`beam${i}`} x1={x} y1={box.y} x2={x} y2={box.y + box.h - 6} stroke="#5A3D26" strokeWidth={1.6} />);
    }
  }
  return out;
}

const GROUND = 100;

function exteriorShapes(stage: HomeStageId, look: HomeLook): ReactNode[] {
  const wall = wallHex(look.wallColor);
  const roof = roofHex(look.roofColor);
  const out: ReactNode[] = [];
  const house = (box: Box, rows: number, cols: number, roofPts: string | null) => {
    out.push(<rect key="body" x={box.x} y={box.y} width={box.w} height={box.h} fill={wall} stroke={shade(wall, 0.7)} strokeWidth={0.8} />);
    out.push(...wallDetails(box, look));
    if (roofPts) out.push(<polygon key="roof" points={roofPts} fill={roof} stroke={shade(roof, 0.7)} strokeWidth={0.8} />);
    out.push(...windows(box, rows, cols, look, true));
    out.push(door(box.x + box.w / 2, GROUND, look));
  };
  switch (stage) {
    case "tent":
      out.push(<polygon key="tent" points={`36,${GROUND} 80,44 124,${GROUND}`} fill={wall} stroke={shade(wall, 0.65)} strokeWidth={1} />);
      out.push(<polygon key="flap" points={`70,${GROUND} 80,62 90,${GROUND}`} fill={roof} />);
      out.push(<line key="pole" x1={80} y1={40} x2={80} y2={46} stroke="#5A3D26" strokeWidth={1.5} />);
      break;
    case "log_cabin": {
      const box = { x: 40, y: 62, w: 80, h: 38 };
      out.push(<rect key="body" x={box.x} y={box.y} width={box.w} height={box.h} fill={wall} stroke={shade(wall, 0.7)} strokeWidth={0.8} />);
      for (let y = box.y + 5; y < GROUND; y += 5) out.push(<line key={`log${y}`} x1={box.x} y1={y} x2={box.x + box.w} y2={y} stroke={shade(wall, 0.72)} strokeWidth={0.8} />);
      out.push(<polygon key="roof" points={`32,64 80,34 128,64`} fill={roof} stroke={shade(roof, 0.7)} strokeWidth={0.8} />);
      out.push(...windows({ x: 40, y: 64, w: 80, h: 36 }, 1, 3, look, true));
      out.push(door(80, GROUND, look));
      break;
    }
    case "trailer_home": {
      out.push(<rect key="body" x={20} y={64} width={120} height={28} rx={6} fill={wall} stroke={shade(wall, 0.7)} strokeWidth={0.8} />);
      out.push(<rect key="roof" x={22} y={60} width={116} height={6} rx={3} fill={roof} />);
      out.push(<rect key="stripe" x={20} y={80} width={120} height={3} fill={roof} opacity={0.7} />);
      out.push(...windows({ x: 26, y: 66, w: 108, h: 14 }, 1, 4, look, false));
      out.push(<circle key="wheel1" cx={46} cy={95} r={5} fill="#2B2F36" />);
      out.push(<circle key="wheel2" cx={114} cy={95} r={5} fill="#2B2F36" />);
      out.push(door(130, 92, look, 8, 18));
      break;
    }
    case "small_house":
      house({ x: 45, y: 58, w: 70, h: 42 }, 2, 3, `38,60 80,28 122,60`);
      break;
    case "medium_house":
      house({ x: 34, y: 46, w: 92, h: 54 }, 2, 3, `27,48 50,20 110,20 133,48`);
      break;
    case "mansion":
      house({ x: 14, y: 40, w: 132, h: 60 }, 3, 5, `7,42 32,14 128,14 153,42`);
      out.push(<rect key="col1" x={68} y={70} width={3} height={30} fill="#FFFFFF" />);
      out.push(<rect key="col2" x={89} y={70} width={3} height={30} fill="#FFFFFF" />);
      break;
    case "castle": {
      const keep = { x: 42, y: 40, w: 76, h: 60 };
      for (const tx of [16, 120]) {
        out.push(<rect key={`tower${tx}`} x={tx} y={30} width={24} height={70} fill={wall} stroke={shade(wall, 0.7)} strokeWidth={0.8} />);
        out.push(<polygon key={`cone${tx}`} points={`${tx - 3},31 ${tx + 12},6 ${tx + 27},31`} fill={roof} stroke={shade(roof, 0.7)} strokeWidth={0.8} />);
        out.push(<rect key={`slit${tx}`} x={tx + 10} y={50} width={4} height={10} fill="#2B3440" />);
      }
      out.push(<rect key="keep" x={keep.x} y={keep.y} width={keep.w} height={keep.h} fill={wall} stroke={shade(wall, 0.7)} strokeWidth={0.8} />);
      for (let i = 0; i < 7; i++) out.push(<rect key={`cren${i}`} x={keep.x + 2 + i * 11} y={keep.y - 6} width={6} height={6} fill={wall} stroke={shade(wall, 0.7)} strokeWidth={0.6} />);
      out.push(...wallDetails(keep, look));
      out.push(...windows({ x: keep.x, y: keep.y + 4, w: keep.w, h: 30 }, 1, 3, look, false));
      out.push(<path key="gate" d={`M70 ${GROUND} V78 A10 10 0 0 1 90 78 V${GROUND} Z`} fill={shade(roof, 0.9)} />);
      break;
    }
  }
  return out;
}

export function HomeExteriorPreview({ stage, look, size = 160, label }: { stage: HomeStageId; look: HomeLook; size?: number; label?: string }) {
  const description = label ?? `Preview: ${stageLabel(stage)}, ${exteriorLookSummary(look).toLowerCase()}`;
  return (
    <svg className="home-preview" viewBox="0 0 160 110" width={size} height={(size * 110) / 160} role="img" aria-label={description} data-testid="home-preview">
      <rect x={0} y={0} width={160} height={110} fill="#DCEBF7" />
      <rect x={0} y={GROUND} width={160} height={10} fill="#8DBF6A" />
      {exteriorShapes(stage, look)}
    </svg>
  );
}

export function HomeInteriorPreview({ look, size = 160 }: { look: HomeLook; size?: number }) {
  const wall = wallHex(look.interiorWallColor);
  const floor = floorHex(look.floor);
  const lines: ReactNode[] = [];
  if (look.floor === "wood") for (let i = 1; i < 8; i++) lines.push(<line key={`f${i}`} x1={i * 20} y1={64} x2={i * 20 + (i - 4) * 6} y2={100} stroke={shade(floor, 0.8)} strokeWidth={0.8} />);
  if (look.floor === "tile") {
    for (let i = 1; i < 6; i++) lines.push(<line key={`t${i}`} x1={i * 27} y1={64} x2={i * 27 + (i - 3) * 8} y2={100} stroke={shade(floor, 0.85)} strokeWidth={0.8} />);
    for (const y of [74, 86]) lines.push(<line key={`h${y}`} x1={0} y1={y} x2={160} y2={y} stroke={shade(floor, 0.85)} strokeWidth={0.8} />);
  }
  if (look.floor === "stone") for (const [x, y] of [[20, 72], [60, 80], [104, 70], [136, 88], [40, 92], [90, 94]] as const) lines.push(<ellipse key={`s${x}`} cx={x} cy={y} rx={9} ry={4} fill="none" stroke={shade(floor, 0.75)} strokeWidth={0.8} />);
  return (
    <svg
      className="home-preview"
      viewBox="0 0 160 100"
      width={size}
      height={(size * 100) / 160}
      role="img"
      aria-label={`Preview of the inside: ${interiorLookSummary(look).toLowerCase()}`}
      data-testid="room-preview"
    >
      <rect x={0} y={0} width={160} height={64} fill={wall} />
      <polygon points="0,0 18,10 18,64 0,64" fill={shade(wall, 0.88)} />
      <polygon points="160,0 142,10 142,64 160,64" fill={shade(wall, 0.88)} />
      <rect x={60} y={18} width={40} height={26} fill="#BCD9F0" stroke="#FFFFFF" strokeWidth={2} />
      <polygon points="0,64 160,64 160,100 0,100" fill={floor} />
      {lines}
    </svg>
  );
}
